import { useEffect, useState } from "react";
import { adminApi, JOB_KIND_LABEL, type QueueSnapshot, type QueueJob } from "@/lib/admin";
import { formatPln } from "@/lib/utils";

export function AdminQueue() {
  const [snap, setSnap] = useState<QueueSnapshot | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [working, setWorking] = useState(false);

  async function refresh() {
    try {
      const s = await adminApi.getQueue();
      setSnap(s);
      setErr(null);
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  useEffect(() => {
    refresh();
    const t = window.setInterval(refresh, 1500);
    return () => window.clearInterval(t);
  }, []);

  async function action(fn: () => Promise<unknown>) {
    setWorking(true);
    try {
      await fn();
      await refresh();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setWorking(false);
    }
  }

  if (!snap) return <p className="font-mono text-sm">{err ?? "Ładuję kolejkę…"}</p>;

  return (
    <div className="space-y-5">
      <header className="flex items-baseline justify-between flex-wrap gap-3">
        <h1 className="font-display font-bold text-3xl">Kolejka odtworzeń</h1>
        <p className="text-xs font-mono text-carp-ink/60">
          live + replay · {snap.pending.length} pending · auto-refresh 1.5s
        </p>
      </header>

      {err && <div className="brut bg-carp-destructive text-white p-3 text-sm">{err}</div>}

      <section className="brut bg-carp-paper p-5 flex items-center justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs font-display font-bold uppercase text-carp-ink/60">Stan kolejki</p>
          <p className="font-display font-bold text-2xl mt-1">
            {snap.paused ? (
              <span className="text-carp-destructive">⏸ PAUZA</span>
            ) : (
              <span className="text-carp-lime">▶ DZIAŁA</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {snap.paused ? (
            <button
              type="button"
              className="btn-primary"
              disabled={working}
              onClick={() => action(adminApi.resumeQueue)}
            >
              ▶ Wznów
            </button>
          ) : (
            <button
              type="button"
              className="btn-ghost !bg-carp-destructive !text-white"
              disabled={working}
              onClick={() => action(adminApi.pauseQueue)}
            >
              ⏸ Zatrzymaj
            </button>
          )}
          {snap.pending.length > 0 && (
            <button
              type="button"
              className="btn-ghost text-sm"
              disabled={working}
              onClick={() => {
                if (confirm(`Wyrzucić ${snap.pending.length} zadań z kolejki?`)) action(adminApi.clearQueue);
              }}
            >
              ✗ Wyczyść kolejkę
            </button>
          )}
        </div>
      </section>

      {snap.current && (
        <section className="brut bg-carp-cyan p-5 space-y-3">
          <p className="text-xs font-display font-bold uppercase text-carp-ink/70 mb-1">
            Teraz odtwarzane · {JOB_KIND_LABEL[snap.current.kind]}
          </p>
          <JobLine job={snap.current} highlight />
          {snap.current.steps?.length > 0 && (
            <details open className="font-mono text-[11px] bg-carp-ink/10 p-2 max-h-64 overflow-auto">
              <summary className="cursor-pointer font-display text-xs uppercase">timeline ({snap.current.steps.length})</summary>
              <ol className="mt-1 space-y-0.5 leading-tight">
                {snap.current.steps.map((s, i) => <li key={i}>{s}</li>)}
              </ol>
            </details>
          )}
        </section>
      )}

      <section>
        <h2 className="font-display font-bold text-xl mb-2">
          Pending ({snap.pending.length})
        </h2>
        {snap.pending.length === 0 ? (
          <p className="text-sm font-mono text-carp-ink/50 brut bg-carp-paper p-4 text-center">
            Kolejka pusta
          </p>
        ) : (
          <ol className="space-y-2">
            {snap.pending.map((job, idx) => (
              <li key={job.id} className="brut bg-carp-paper p-3 flex items-center gap-3">
                <span className="chip bg-carp-yellow font-mono">{idx + 1}</span>
                <div className="flex-1">
                  <JobLine job={job} />
                </div>
                <button
                  type="button"
                  className="btn-ghost text-xs"
                  disabled={working}
                  onClick={() => action(() => adminApi.removeJob(job.id))}
                  title="Pomiń ten donejt — zostanie oznaczony jako skipped"
                >
                  ⏭ pomiń
                </button>
              </li>
            ))}
          </ol>
        )}
      </section>

      {snap.recentlyDone.length > 0 && (
        <section>
          <h2 className="font-display font-bold text-xl mb-2">Ostatnie ({snap.recentlyDone.length})</h2>
          <ol className="space-y-2">
            {snap.recentlyDone.map((job) => (
              <li key={job.id} className="brut bg-carp-paper/60 p-2 text-sm">
                <JobLine job={job} compact />
                {job.steps?.length > 0 && (
                  <details className="font-mono text-[10px] bg-carp-ink/5 mt-1 p-1.5">
                    <summary className="cursor-pointer text-carp-ink/60">timeline ({job.steps.length}) · ostatni: {job.steps[job.steps.length - 1]}</summary>
                    <ol className="mt-1 space-y-0.5 leading-tight max-h-48 overflow-auto">
                      {job.steps.map((s, i) => <li key={i}>{s}</li>)}
                    </ol>
                  </details>
                )}
                {job.error && (
                  <p className="text-xs font-mono text-carp-destructive mt-1">⚠ {job.error}</p>
                )}
              </li>
            ))}
          </ol>
        </section>
      )}
    </div>
  );
}

function JobLine({ job, highlight = false, compact = false }: { job: QueueJob; highlight?: boolean; compact?: boolean }) {
  const statusBg =
    job.status === "running" ? "bg-carp-cyan" :
    job.status === "done" ? "bg-carp-lime" :
    job.status === "failed" ? "bg-carp-destructive text-white" :
    job.status === "skipped" ? "bg-carp-ink/20" :
    "bg-carp-yellow";

  return (
    <div className={`flex items-center justify-between gap-3 ${highlight ? "font-display font-bold text-lg" : "text-sm"}`}>
      <div className="flex-1 min-w-0">
        <span className={`chip ${statusBg} mr-2`}>{job.status}</span>
        <span className="chip mr-2">{JOB_KIND_LABEL[job.kind]}</span>
        <b>{job.donation.donorName}</b> {formatPln(job.donation.amount)}
        {job.donation.message && !compact && (
          <span className="ml-2 italic text-carp-ink/70 truncate">„{job.donation.message.slice(0, 60)}{job.donation.message.length > 60 ? "…" : ""}"</span>
        )}
      </div>
      <span className="text-xs font-mono text-carp-ink/50 whitespace-nowrap">
        {new Date(job.queuedAt).toLocaleTimeString("pl-PL")}
      </span>
    </div>
  );
}
